import React, { useState, useEffect } from "react";
import VendorList from "../components/VendorList";
import StatusBadge from "../components/StatusBadge";
import RequestHistory from "../components/RequestHistory";
import { createPortal } from 'react-dom';
import vendorStyles from '../components/VendorList.module.css';
import FeatureHeader from '../components/FeatureHeader';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const getValidS3Url = (url) => {
  if (!url) return null;
  return url.replace("s3.ap-south-1", "s3-ap-southeast-1");
};
const getValidDeckUrl = getValidS3Url;

// =========================================================
// ✅ HARDCODED CONTENT
// =========================================================
const HARDCODED_TESTIMONIALS = [
    {
        review: "Getting packaging design done through SSN was faster than hiring on LinkedIn. And the rates? Unreal!",
        author: "A Thrive Brand Partner",
        brand: "Thrive Brand Partner",
    },
    {
        review: "SSN made vendor selection a no-brainer. Trusted, efficient, and no back-and-forth.",
        author: "A Bloom Brand Partner",
        brand: "Bloom Brand Partner",
    },
    // Add more testimonials here if needed
];

const HARDCODED_FAQS = [
    {
        question: "Is this a paid service?",
        answer: "No. Access to SSN is included as part of your Suspire plan.",
    },
    {
        question: "Does Suspire take a commission?",
        answer: "Never. You work directly with the vendor.",
    },
    {
        question: "Are the vendors trustworthy?",
        answer: "All partners are pre-vetted for quality, pricing, and alignment with our platform.",
    },
    {
        question: "Will I get better pricing if I upgrade my plan?",
        answer: "Yes. Bloom and Thrive partners get exclusive rate advantages.",
    },
    {
        question: "Can I bring my own service provider?",
        answer: "Absolutely. SSN complements your in-house or external team.",
    },
    {
        question: "How fast can I get a response?",
        answer: "Most vendors respond within 24-48 hrs of your request.",
    },
    {
        question: "Do vendors sign NDAs or contracts?",
        answer: "Yes, directly with you. Suspire does not mediate.",
    },
    {
        question: "How often are vendors reviewed?",
        answer: "Monthly based on performance, responsiveness, and feedback.",
    },
    {
        question: "Will I see rates upfront?",
        answer: "No. Rates are revealed post-request and depend on your Suspire plan.",
    },
    {
        question: "What happens if I face an issue?",
        answer: "We collect feedback and take action quickly, including delisting non-performing vendors.",
    },
];

// =========================================================
// 🌱 Main Component
// =========================================================
const SuspireSupportNetwork = () => {
    const [vendors, setVendors] = useState([]);
    const [banner, setBanner] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [showConciergeForm, setShowConciergeForm] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null); 
    const [nextPlan, setNextPlan] = useState(null);
    const [isSetupComplete, setIsSetupComplete] = useState(false);
    const [status, setStatus] = useState("");
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        email: "",
        serviceType: "",
        description: "",
        preferredDate: "",
        additionalNotes: "",
    });
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [filteredServices, setFilteredServices] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null); 
    const [redeemEmail, setRedeemEmail] = useState("");
    const [redeemMessage, setRedeemMessage] = useState("");
    const [serviceMessages, setServiceMessages] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessages, setSuccessMessages] = useState([]);

    // =========================================================
    // ✅ Fetch Banner (Kept API call)
    // =========================================================
    useEffect(() => {
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        fetch(`${API_BASE}/api/ssn/banners/`, { headers })
            .then((res) => res.json())
            .then((data) => {
                if (data?.is_active) setBanner(data);
            })
            .catch((err) => console.error("Error fetching banner:", err));
    }, []);

    // =========================================================
    // ✅ Fetch Vendors (Updated with search)
    // =========================================================
    useEffect(() => {
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        // Build query parameters
        const params = new URLSearchParams();
        if (debouncedSearch) params.append('search', debouncedSearch);
        params.append('include_services', 'true'); // Add this to get services data
        
        fetch(`${API_BASE}/api/ssn/vendors/?${params}`, { headers })
            .then((res) => {
                console.log('🔍 SSN Vendor Search Response Status:', res.status);
                console.log('🔍 SSN Vendor Search Response Headers:', [...res.headers.entries()]);
                return res.json();
            })
            .then((data) => {
                console.log('🔍 SSN Vendor Search Response Data:', data);
                console.log('🔍 Data type:', typeof data);
                console.log('🔍 Data length:', Array.isArray(data) ? data.length : 'Not an array');
                console.log('🔍 First vendor sample:', Array.isArray(data) && data.length > 0 ? data[0] : 'No data');
                
                //✅ Handle different response structures
                let vendorList = [];
                if (Array.isArray(data)) {
                    // Direct array response
                    vendorList = data;
                } else if (data && typeof data === 'object') {
                    // Paginated response or object with vendors array
                    if (Array.isArray(data.results)) {
                        vendorList = data.results;
                    } else if (Array.isArray(data.vendors)) {
                        vendorList = data.vendors;
                    } else if (data.count !== undefined && Array.isArray(data[data.count > 0 ? Object.keys(data).find(k => Array.isArray(data[k]) && k !== 'count') : ''])) {
                        // Find the first array property that's not 'count'
                        const arrayKey = Object.keys(data).find(k => Array.isArray(data[k]) && k !== 'count');
                        if (arrayKey) {
                            vendorList = data[arrayKey];
                        }
                    }
                }
                
                console.log('🔍 Extracted vendor list:', vendorList);
                console.log('🔍 Extracted vendor count:', vendorList.length);
                
                // Check if vendors have services data, if not, fetch services separately
                const hasServices = vendorList.length > 0 && vendorList.some(v => v.services && v.services.length > 0);
                console.log('🔍 Vendors have services data:', hasServices);
                
                if (!hasServices && vendorList.length > 0) {
                    // Fetch services for each vendor separately
                    console.log('🔍 Fetching services separately for each vendor...');
                    const token = localStorage.getItem("token");
                    const headers = { "Content-Type": "application/json" };
                    if (token) headers["Authorization"] = `Bearer ${token}`;
                    
                    Promise.all(
                        vendorList.map(vendor => 
                            fetch(`${API_BASE}/api/ssn/vendors/${vendor.id}/`, { headers })
                                .then(res => res.json())
                                .then(vendorData => ({
                                    ...vendor,
                                    services: vendorData.services || []
                                }))
                                .catch(err => {
                                    console.error('Error fetching services for vendor:', vendor.id, err);
                                    return { ...vendor, services: [] };
                                })
                        )
                    ).then(vendorsWithServices => {
                        console.log('🔍 Vendors with services:', vendorsWithServices);
                        setVendors(vendorsWithServices);
                        setIsLoading(false);
                    });
                } else {
                    setVendors(vendorList);
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                console.error("Error fetching vendors:", err);
                setIsLoading(false);
            });
    }, [debouncedSearch]);

    useEffect(() => {
        const handleOpenVendorModal = (event) => {
            console.log('🎯 Vendor modal event received:', event.detail);
            setSelectedVendor(event.detail);
        };
        
        window.addEventListener('openVendorModal', handleOpenVendorModal);
        
        return () => {
            window.removeEventListener('openVendorModal', handleOpenVendorModal);
        };
    }, []);

    const handleRedeem = (service, vendor) => {
        const token = localStorage.getItem('token');
        
        const combinedMessage = `${
            redeemMessage ? redeemMessage + "\n\n---\nGeneral Message Above\n\n" : ""
        }${serviceMessages[service.id] || ""}`;

        if (!redeemEmail) {
            setErrorMessage("❌ Email is required to redeem this service.");
            setSuccessMessages([]);
            return;
        }

        fetch(`${API_BASE}/api/ssn/vendors/${vendor.id}/services/${service.id}/redeem/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ email: redeemEmail, message: combinedMessage }),
        })
            .then((res) => {
                if (!res.ok) {
                    return res.json().then((err) => {
                        throw new Error(err.error || "Failed to redeem service");
                    });
                }
                return res.json();
            })
            .then((data) => {
                setErrorMessage("");
                setSuccessMessages(prev => [
                    ...prev,
                    <div key={data.id || Date.now()}>
                        ✅ Redeem request sent for: {service.service_name}<br />
                        Status: <StatusBadge status={data.status || 'pending'} />
                        <p>We'll review your request and get back to you soon.</p>
                    </div>
                ]);
                setServiceMessages((prev) => ({ ...prev, [service.id]: "" }));
            })
            .catch((err) => {
                setSuccessMessages([]);
                setErrorMessage("❌ " + err.message);
                console.error(err);
            });
    };

    // =========================================================
    // ✅ Initial Setup Check (Kept API call)
    // =========================================================
    useEffect(() => {
        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        fetch(`${API_BASE}/api/auth/subscription-plans/`, { headers })
            .then(() => setIsSetupComplete(true))
            .catch(() => setIsSetupComplete(true));
    }, []);

    // =========================================================
    // ✅ Determine Next Plan (Kept)
    // =========================================================
    const determineNextPlan = (currentPlanName) => {
        const plans = ["seed", "root", "bloom", "thrive"];
        const normalizedPlan = currentPlanName?.trim().toLowerCase();
        const currentIdx = plans.indexOf(normalizedPlan);

        if (currentIdx === -1 || currentIdx === plans.length - 1) {
            setNextPlan(null); 
            return;
        }
        const next = plans[currentIdx + 1];
        setNextPlan(next.charAt(0).toUpperCase() + next.slice(1));
    };

    // =========================================================
    // ✅ Fetch Current Plan (Kept API call)
    // =========================================================
    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const token = localStorage.getItem("token");
                const headers = { "Content-Type": "application/json" };
                if (token) headers["Authorization"] = `Bearer ${token}`;

                const response = await fetch(`${API_BASE}/api/onboarding/subscription-status/`, { headers });

                if (!response.ok) {
                    setCurrentPlan(null);
                    determineNextPlan(null);
                    return;
                }

                const data = await response.json().catch(() => ({}));
                const planName = data?.current_plan || data?.plan || data?.name;

                if (planName) {
                    const plan = planName.trim().toLowerCase();
                    setCurrentPlan(plan);
                    determineNextPlan(plan);
                } else {
                    setCurrentPlan(null);
                    determineNextPlan(null);
                }
            } catch (err) {
                console.error("Error fetching plan:", err);
                setCurrentPlan(null);
                determineNextPlan(null);
            }
        };
        fetchPlan();
    }, []);

    // =========================================================
    // ✅ Filter Vendors (Updated with search)
    // =========================================================
    const vendorTypes = Array.from(
        new Set(vendors.map((v) => v.category?.trim()).filter((c) => c))
    );

    // Enhanced search filtering to separate vendor matches from service matches
    const searchFilteredVendors = React.useMemo(() => {
        console.log("=== SEARCH DEBUG START ===");
        console.log("Debounced Search:", debouncedSearch);
        console.log("Vendors Data:", vendors);
        console.log("First Vendor Structure:", vendors[0]);
        
        const categoryFiltered = selectedCategory === "all"
            ? vendors
            : vendors.filter(
                  (v) =>
                      v.category &&
                      v.category.trim().toLowerCase() === selectedCategory
              );
        
        if (!debouncedSearch) {
            setFilteredServices([]);
            return categoryFiltered;
        }

        const query = debouncedSearch.toLowerCase();
        const matchingServices = [];
        
        const filtered = categoryFiltered.filter((vendor) => {
            console.log("Processing vendor:", vendor.name);
            console.log("Vendor services:", vendor.services);
            
            const matchesVendorName = vendor.name && vendor.name.toLowerCase().includes(query);
            console.log("Matches vendor name:", matchesVendorName);
            
            const matchesService = vendor.services && vendor.services.some((service) => {
                console.log("Processing service:", service);
                console.log("Service type:", typeof service);
                console.log("Service keys:", service ? Object.keys(service) : 'null');
                
                // Check if service has service_name property
                const serviceName = service.service_name || service.name || service;
                console.log("Extracted service name:", serviceName);
                
                const serviceMatches = serviceName && serviceName.toLowerCase().includes(query);
                console.log("Service matches:", serviceMatches);
                
                if (serviceMatches) {
                    matchingServices.push({ 
                        vendorName: vendor.name, 
                        vendorCategory: vendor.category,
                        service: serviceName,
                        vendorId: vendor.id,
                        serviceId: service.id,
                        serviceDescription: service.short_description || service.description
                    });
                    console.log("Added to matching services:", serviceName);
                }
                return serviceMatches;
            });
            
            console.log("Vendor matches overall:", matchesVendorName || matchesService);
            return matchesVendorName || matchesService;
        });

        console.log("Final matching services:", matchingServices);
        console.log("Final filtered vendors:", filtered);
        console.log("=== SEARCH DEBUG END ===");

        setFilteredServices(matchingServices);
        return filtered;
    }, [vendors, selectedCategory, debouncedSearch]);

    // =========================================================
    // ✅ Concierge Form Logic (Updated with status)
    // =========================================================
    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("Submitting...");

        const token = localStorage.getItem("token");
        if (!token) {
            setStatus("❌ Please log in again.");
            return;
        }

        const payload = {
            service_name: formData.serviceType,
            description: formData.description,
            preferred_date: formData.preferredDate,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            additional_notes: formData.additionalNotes,
        };

        const response = await fetch(`${API_BASE}/api/ssn/concierge-request/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (response.ok) {
            const responseData = await response.json();
            setStatus(
                <div>
                    ✅ Request submitted successfully!<br />
                    Status: <StatusBadge status={responseData.status || 'pending'} />
                    <p>We'll review your request and get back to you soon.</p>
                </div>
            );
            setFormData({
                name: "",
                phone: "",
                email: "",
                serviceType: "",
                description: "",
                preferredDate: "",
                additionalNotes: "",
            });

            setTimeout(() => {
                setStatus("");
                setShowConciergeForm(false);
            }, 3000);
        } else {
            setStatus("❌ Failed to submit request.");
        }
    };

    // Debounce search function
    useEffect(() => {
        setIsLoading(true);
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300);
        
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // =========================================================
    // 🧭 Render
    // =========================================================
    return (
        <div style={{ padding: "20px" }}>
            {/* ⭐ PAGE TITLE + SUBTITLE ⭐ */}
            <div
                style={{
                    maxWidth: "1150px",
                    margin: "0 auto 30px auto",
                    textAlign: "left",
                    padding: "20px 10px",
                }}
            >
                <FeatureHeader
                    src="/images/feature-headers/support-network.png"
                    alt="Suspire Support Network"
                    align="left"
                    style={{ marginBottom: "12px" }}
                />

                <p
                    style={{
                        fontSize: "18px",
                        fontWeight: "400",
                        margin: "0",
                        color: "rgba(0,0,0,0.6)",
                    }}
                >
                    Your ecosystem of Suspire-verified service partners.
                </p>
            </div>

            {/* ⭐ HOW IT WORKS ⭐ */}
            <div
                style={{
                    maxWidth: "1150px",
                    margin: "40px auto",
                    padding: "20px",
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.25)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                }}
            >
                <h2
                    style={{
                        fontSize: "26px",
                        fontWeight: "700",
                        textAlign: "center",
                        marginBottom: "25px",
                    }}
                >
                    How It Works
                </h2>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "20px",
                    }}
                >
                    <div style={{ flex: "1", minWidth: "250px", textAlign: "center" }}>
                        <div style={{ fontSize: "40px" }}>🔍</div>
                        <h3 style={{ margin: "10px 0" }}>Find the service you need</h3>
                    </div>

                    <div style={{ flex: "1", minWidth: "250px", textAlign: "center" }}>
                        <div style={{ fontSize: "40px" }}>✅</div>
                        <h3 style={{ margin: "10px 0" }}>Choose from vetted vendors</h3>
                    </div>

                    <div style={{ flex: "1", minWidth: "250px", textAlign: "center" }}>
                        <div style={{ fontSize: "40px" }}>📞</div>
                        <h3 style={{ margin: "10px 0" }}>Request a call or rates</h3>
                    </div>
                </div>
            </div>

            {/* ⭐ FEATURE COMPARISON ⭐ */}
            <div
                style={{
                    maxWidth: "1150px",
                    margin: "40px auto",
                    padding: "20px",
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.25)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                }}
            >
                <h2
                    style={{
                        fontSize: "26px",
                        fontWeight: "700",
                        textAlign: "center",
                        marginBottom: "25px",
                    }}
                >
                    SSN vs. Typical Freelancer Platforms
                </h2>

                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "16px",
                    }}
                >
                    <thead>
                        <tr style={{ background: "rgba(0,0,0,0.05)" }}>
                            <th style={{ padding: "12px", textAlign: "left" }}>Feature</th>
                            <th style={{ padding: "12px", textAlign: "center" }}>
                                Suspire SSN
                            </th>
                            <th style={{ padding: "12px", textAlign: "center" }}>
                                Freelancer Platforms
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td style={{ padding: "12px" }}>Expert Quality</td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ✔️ Curated, verified experts
                            </td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ❌ Anyone can list
                            </td>
                        </tr>
                        <tr style={{ background: "rgba(0,0,0,0.03)" }}>
                            <td style={{ padding: "12px" }}>Values</td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ✔️ Sustainability aligned
                            </td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ❌ Generic platforms
                            </td>
                        </tr>
                        <tr>
                            <td style={{ padding: "12px" }}>Speed</td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ✔️ Fast access
                            </td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ❌ Slow bidding
                            </td>
                        </tr>
                        <tr style={{ background: "rgba(0,0,0,0.03)" }}>
                            <td style={{ padding: "12px" }}>Commission</td>
                            <td style={{ padding: "12px", textAlign: "center" }}>✔️ None</td>
                            <td style={{ padding: "12px", textAlign: "center" }}>
                                ❌ High fees
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* 🖼️ Banner */}
            {banner && (
                <div
                    style={{
                        marginBottom: "25px",
                        width: "100%",
                        maxWidth: "1150px",
                        marginInline: "auto",
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                    }}
                >
                    <img
                        src={banner.image}
                        alt={banner.title}
                        style={{
                            width: "100%",
                            height: "200px",
                            objectFit: "cover",
                        }}
                    />
                </div>
            )}

            {/* 🔘 Top Buttons/Plan Info with Search */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    padding: "15px 25px",
                    marginBottom: "25px",
                    width: "100%",
                    maxWidth: "1150px",
                    marginInline: "auto",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
            >
                {/* Left Side: Search Input */}
                <div style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    minWidth: "400px",
                }}>
                    <span style={{
                        marginRight: "8px",
                        fontSize: "16px",
                        color: "#6B7280",
                    }}>
                        🔍
                    </span>
                    <input
                        type="text"
                        placeholder="Search vendors by name or service..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            flex: 1,
                            height: "44px",
                            border: "none",
                            outline: "none",
                            fontSize: "16px",
                            backgroundColor: "transparent",
                        }}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            style={{
                                marginLeft: "8px",
                                background: "none",
                                border: "none",
                                fontSize: "18px",
                                cursor: "pointer",
                                color: "#6B7280",
                            }}
                        >
                            ×
                        </button>
                    )}
                </div>

                {/* Center: Dynamic Content based on Plan */}
                <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                    {/* 1. If Plan is THRIVE: Show Plan Benefit Text */}
                    {currentPlan === "thrive" && (
                        <p style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#1a3c34",
                        }}>
                            Your Thrive plan gives you access to best pricing available!
                        </p>
                    )}

                    {/* 2. If Plan is ROOT or BLOOM or SEED: Show Upgrade Button */}
                    {nextPlan && currentPlan !== "thrive" && (
                        <button
                            onClick={() => (window.location.href = "/subscriptions")}
                            style={{
                                padding: "10px 24px",
                                fontSize: "16px",
                                borderRadius: "30px",
                                border: "none",
                                cursor: "pointer",
                                background: "#28a745",
                                color: "#fff",
                                fontWeight: "600",
                                boxShadow: "0 4px 10px rgba(40,167,69,0.3)",
                            }}
                        >
                            Upgrade to {nextPlan}
                        </button>
                    )}
                </div>

                {/* Right Side: Request Concierge Button */}
                <button
                    onClick={() => setShowConciergeForm(true)}
                    style={{
                        padding: "10px 24px",
                        fontSize: "16px",
                        borderRadius: "30px",
                        border: "none",
                        cursor: "pointer",
                        background: "#007bff",
                        color: "#fff",
                        fontWeight: "600",
                        boxShadow: "0 4px 10px rgba(0,123,255,0.3)",
                        whiteSpace: "nowrap",
                    }}
                >
                    Request Concierge Services
                </button>
            </div>

            {/* 🧩 Vendor List Section */}
            {isSetupComplete ? (
                <>
                    {/* Category Filter */}
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "12px",
                            marginBottom: "25px",
                            padding: "15px 20px",
                            background: "rgba(255, 255, 255, 0.2)",
                            backdropFilter: "blur(10px)",
                            borderRadius: "12px",
                            width: "100%",
                            maxWidth: "1150px",
                            marginInline: "auto",
                        }}
                    >
                        <button
                            onClick={() => setSelectedCategory("all")}
                            style={{
                                padding: "12px 24px",
                                fontSize: "16px",
                                borderRadius: "30px",
                                background:
                                    selectedCategory === "all" ? "#28a745" : "#f0f0f0",
                                color: selectedCategory === "all" ? "#fff" : "#333",
                                fontWeight: "700",
                            }}
                        >
                            All
                        </button>

                        {vendorTypes.map((type) => {
                            const normalized = type.trim().toLowerCase();
                            return (
                                <button
                                    key={type}
                                    onClick={() => setSelectedCategory(normalized)}
                                    style={{
                                        padding: "12px 24px",
                                        fontSize: "16px",
                                        borderRadius: "30px",
                                        background:
                                            selectedCategory === normalized
                                                ? "#28a745"
                                                : "#f0f0f0",
                                        color:
                                            selectedCategory === normalized
                                                ? "#fff"
                                                : "#333",
                                        fontWeight: "700",
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {type}
                                </button>
                            );
                        })}
                    </div>

                    {/* Vendors Grid */}
                    {isLoading ? (
                        <div style={{
                            textAlign: "center",
                            padding: "20px",
                            maxWidth: "1150px",
                            margin: "0 auto",
                        }}>
                            Loading vendors...
                        </div>
                    ) : debouncedSearch ? (
                        // When searching, check if we have results
                        (filteredServices.length === 0 && searchFilteredVendors.length === 0) ? (
                            // No results found
                            <div style={{
                                textAlign: "center",
                                padding: "40px 20px",
                                maxWidth: "1150px",
                                margin: "0 auto",
                                background: "rgba(255, 255, 255, 0.2)",
                                borderRadius: "12px",
                            }}>
                                <div style={{ fontSize: "36px", marginBottom: "10px" }}>🔍</div>
                                <h3>No results found for "{debouncedSearch}"</h3>
                                <p style={{ marginBottom: "20px" }}>Try different keywords or clear filters</p>
                                <button
                                    onClick={() => {
                                        setSearchQuery("");
                                        setDebouncedSearch("");
                                    }}
                                    style={{
                                        padding: "10px 20px",
                                        backgroundColor: "#007bff",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Clear Search
                                </button>
                            </div>
                        ) : null // Has results, so don't show anything here - Service/Partner matches will show below
                    ) : (
                        // When NOT searching, show VendorList
                        <VendorList vendors={searchFilteredVendors} />
                    )}

                    {/* Service Results Section - Shows only when search matches services */}
                    {filteredServices.length > 0 && debouncedSearch && (
                        <div style={{
                            maxWidth: "1150px",
                            margin: "30px auto 0",
                            padding: "25px",
                            background: "rgba(255, 255, 255, 0.25)",
                            backdropFilter: "blur(10px)",
                            borderRadius: "12px",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        }}>
                            <div style={{
                                alignItems: "center",
                                marginBottom: "20px",
                                gap: "12px",
                            }}>
                                <div>
                                    <h3 style={{
                                        fontSize: "22px",
                                        fontWeight: "700",
                                        color: "#1a3c34",
                                        margin: "0",
                                    }}>
                                        Service Matches for "{debouncedSearch}"
                                    </h3>
                                    <p style={{
                                        fontSize: "14px",
                                        color: "#666",
                                        margin: "4px 0 0 0",
                                    }}>
                                        Found {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''} across our partner network
                                    </p>
                                </div>
                            </div>

                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                gap: "16px",
                            }}>
                                {filteredServices.map((service, index) => (
                                    <div key={`${service.vendorId}-${service.service}-${index}`} style={{
                                        background: "rgba(255, 255, 255, 0.92)",
                                        color: "#1d3557",
                                        border: "1px solid rgba(0,0,0,0.04)",
                                        borderRadius: "12px",
                                        padding: "1.2rem",
                                        cursor: "pointer",
                                        transition: "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
                                        boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
                                        minHeight: "150px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                    }}
                                    onClick={async () => {
                                        // Directly fetch and open vendor modal
                                        const token = localStorage.getItem('token');
                                        try {
                                            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ssn/vendors/${service.vendorId}/`, {
                                                method: "GET",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    "Authorization": `Bearer ${token}`,
                                                }
                                            });
                                            const data = await response.json();
                                            // Trigger modal open by dispatching custom event
                                            const event = new CustomEvent('openVendorModal', { detail: data });
                                            window.dispatchEvent(event);
                                            
                                            // Wait for modal to open and vendor data to load
                                            setTimeout(() => {
                                                // Find and highlight the specific service in the modal
                                                const serviceElement = document.querySelector(`[data-service-id="${service.serviceId}"]`);
                                                if (serviceElement) {
                                                    serviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                    serviceElement.style.backgroundColor = 'rgba(0, 191, 174, 0.2)';
                                                    serviceElement.style.transition = 'background-color 0.3s ease';
                                                    setTimeout(() => {
                                                        serviceElement.style.backgroundColor = '';
                                                    }, 2500);
                                                }
                                            }, 600);
                                        } catch (err) {
                                            console.error("Error fetching vendor details:", err);
                                        }
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-5px) scale(1.02)";
                                        e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.12)";
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.98)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                                        e.currentTarget.style.boxShadow = "0 6px 14px rgba(0,0,0,0.08)";
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.92)";
                                    }}>
                                        <div style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "8px",
                                            width: "100%",
                                        }}>
                                            <h4 style={{
                                                fontSize: "16px",
                                                fontWeight: "600",
                                                color: "#1d3557",
                                                margin: "0",
                                                textAlign: "center",
                                            }}>
                                                {service.service}
                                            </h4>
                                            {service.serviceDescription && (
                                                <p style={{
                                                    fontSize: "13px",
                                                    color: "#666",
                                                    margin: "0",
                                                    lineHeight: "1.4",
                                                    textAlign: "center",
                                                }}>
                                                    {service.serviceDescription.length > 100 
                                                        ? service.serviceDescription.slice(0, 100) + "..."
                                                        : service.serviceDescription
                                                    }
                                                </p>
                                            )}
                                            <div style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "4px",
                                                marginTop: "8px",
                                                alignItems: "center",
                                            }}>
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    justifyContent: "center",
                                                }}>
                                                    <span style={{
                                                        fontSize: "12px",
                                                        color: "#666",
                                                        fontWeight: "500",
                                                    }}>
                                                        Provided by:
                                                    </span>
                                                    <span style={{
                                                        fontSize: "13px",
                                                        color: "#00bfae",
                                                        fontWeight: "600",
                                                    }}>
                                                        {service.vendorName}
                                                    </span>
                                                </div>
                                                {service.vendorCategory && (
                                                    <div style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                        justifyContent: "center",
                                                    }}>
                                                        <span style={{
                                                            fontSize: "12px",
                                                            color: "#666",
                                                            fontWeight: "500",
                                                        }}>
                                                            Category:
                                                        </span>
                                                        <span style={{
                                                            fontSize: "12px",
                                                            background: "rgba(0, 191, 174, 0.1)",
                                                            color: "#00bfae",
                                                            padding: "2px 8px",
                                                            borderRadius: "4px",
                                                            fontWeight: "500",
                                                            textTransform: "capitalize",
                                                        }}>
                                                            {service.vendorCategory}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Partner Matches Section - Shows only when search has results */}
                    {searchFilteredVendors.length > 0 && debouncedSearch ? (
                        <div style={{
                            maxWidth: "1150px",
                            margin: "30px auto 0",
                            padding: "25px",
                            background: "rgba(255, 255, 255, 0.25)",
                            backdropFilter: "blur(10px)",
                            borderRadius: "12px",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        }}>
                            <div style={{
                                alignItems: "center",
                                marginBottom: "20px",
                                gap: "12px",
                            }}>
                                <div>
                                    <h3 style={{
                                        fontSize: "22px",
                                        fontWeight: "700",
                                        color: "#1a3c34",
                                        margin: "0",
                                    }}>
                                        Partner Matches for "{debouncedSearch}"
                                    </h3>
                                    <p style={{
                                        fontSize: "14px",
                                        color: "#666",
                                        margin: "4px 0 0 0",
                                    }}>
                                        Found {searchFilteredVendors.length} partner{searchFilteredVendors.length !== 1 ? 's' : ''} across our partner network
                                    </p>
                                </div>
                            </div>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                gap: "16px",
                            }}>
                                {searchFilteredVendors
                                  .filter((vendor) => vendor && vendor.name)
                                  .map((vendor) => (
                                    <div
                                      key={vendor.id}
                                      style={{
                                        background: "rgba(255, 255, 255, 0.92)",
                                        color: "#1d3557",
                                        border: "1px solid rgba(0,0,0,0.04)",
                                        borderRadius: "12px",
                                        padding: "1.2rem",
                                        cursor: "pointer",
                                        transition: "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
                                        boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
                                        minHeight: "150px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                      }}
                                      onClick={() => {
                                        // Directly open the vendor modal
                                        const token = localStorage.getItem('token');
                                        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ssn/vendors/${vendor.id}/`, {
                                          method: "GET",
                                          headers: {
                                            "Content-Type": "application/json",
                                            "Authorization": `Bearer ${token}`,
                                          }
                                        })
                                        .then(res => res.json())
                                        .then(data => {
                                          // Trigger modal open by setting selected vendor
                                          const event = new CustomEvent('openVendorModal', { detail: data });
                                          window.dispatchEvent(event);
                                        })
                                        .catch(err => console.error("Error fetching vendor details:", err));
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-5px) scale(1.02)";
                                        e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.12)";
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.98)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                                        e.currentTarget.style.boxShadow = "0 6px 14px rgba(0,0,0,0.08)";
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.92)";
                                      }}
                                      data-vendor-id={vendor.id}
                                    >
                                      {vendor.logo && (
                                        <img
                                          src={vendor.logo.replace("s3.ap-south-1", "s3-ap-southeast-1")}
                                          alt={vendor.name}
                                          style={{
                                            width: "50px",
                                            height: "50px",
                                            objectFit: "contain",
                                            marginBottom: "0.5rem",
                                          }}
                                        />
                                      )}
                                      <div style={{
                                        fontWeight: "600",
                                        fontSize: "1rem",
                                        marginBottom: "0.3rem",
                                        color: "#004443",
                                      }}>
                                        {vendor.name}
                                      </div>
                                      <div style={{
                                        fontSize: "0.75rem",
                                        fontWeight: "600",
                                        color: "#2a9d8f",
                                        background: "rgba(42, 157, 143, 0.12)",
                                        padding: "3px 9px",
                                        borderRadius: "7px",
                                        display: "inline-block",
                                        marginBottom: "0.4rem",
                                      }}>
                                        {vendor.category}
                                      </div>
                                      <div style={{
                                        fontSize: "0.8rem",
                                        color: "#666",
                                        lineHeight: "1.3",
                                      }}>
                                        {vendor.description
                                          ? vendor.description.slice(0, 140)
                                          : "No description available."}
                                      </div>
                                    </div>
                                  ))}
                            </div>
                        </div>
                    ) : null}
                </>
            ) : (
                <p style={{ textAlign: "center", marginTop: "50px" }}>
                    Loading Support Network...
                </p>
            )}

            {/* 🤝 TRUST BADGE STATEMENT BLOCK (Hardcoded) 🤝 */}
            <div
                style={{
                    maxWidth: "1150px",
                    margin: "50px auto",
                    padding: "30px",
                    background: "#f0f8ff", // Light background box
                    borderRadius: "14px",
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(0,123,255,0.1)",
                    border: "2px solid #007bff",
                }}
            >
                <h3
                    style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        marginBottom: "10px",
                        color: "#007bff",
                    }}
                >
                    TRUST BADGE STATEMENT BLOCK
                </h3>
                <p style={{ fontSize: "18px", color: "#333", margin: "0" }}>
                    **9 out of 10 Suspire brands rated their SSN vendor 4⭐ or higher.**
                    "We review vendor performance monthly to ensure high-quality experiences."
                </p>
            </div>

            {/* 🗣️ TESTIMONIALS SECTION (Hardcoded) 🗣️ */}
            {HARDCODED_TESTIMONIALS.length > 0 && (
                <div
                    style={{
                        maxWidth: "1150px",
                        margin: "50px auto",
                        padding: "30px",
                        background: "rgba(255,255,255,0.3)",
                        borderRadius: "14px",
                        backdropFilter: "blur(10px)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                    }}
                >
                    <h2
                        style={{
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: "700",
                            marginBottom: "25px",
                        }}
                    >
                        What Brands Say About SSN
                    </h2>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                            gap: "20px",
                        }}
                    >
                        {HARDCODED_TESTIMONIALS.map((t, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: "20px",
                                    borderRadius: "12px",
                                    background: "#fff",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                }}
                            >
                                <p style={{ fontSize: "16px", color: "#333" }}>
                                    “**{t.review}**”
                                </p>
                                <h4
                                    style={{
                                        marginTop: "12px",
                                        fontWeight: "700",
                                        fontSize: "18px",
                                    }}
                                >
                                    — {t.author}
                                </h4>
                                <p
                                    style={{
                                        fontSize: "14px",
                                        color: "rgba(0,0,0,0.6)",
                                    }}
                                >
                                    {t.brand}
                                </p>
                            </div>
                        ))}
                    </div>
                    {/* Note text removed here */}
                </div>
            )}

            {/* ❓ FAQ SECTION (Hardcoded) ❓ */}
            {HARDCODED_FAQS.length > 0 && (
                <div
                    style={{
                        maxWidth: "1150px",
                        margin: "50px auto",
                        padding: "30px",
                        background: "rgba(255,255,255,0.3)",
                        borderRadius: "14px",
                        backdropFilter: "blur(10px)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                    }}
                >
                    <h2
                        style={{
                            textAlign: "center",
                            fontSize: "28px",
                            fontWeight: "700",
                            marginBottom: "25px",
                        }}
                    >
                        Frequently Asked Questions
                    </h2>

                    {HARDCODED_FAQS.map((faq, idx) => (
                        <div
                            key={idx}
                            style={{
                                marginBottom: "20px",
                                padding: "20px",
                                background: "#fff",
                                borderRadius: "12px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                        >
                            <h3 style={{ fontWeight: "700", marginBottom: "8px" }}>
                                {idx + 1}. {faq.question}
                            </h3>
                            <p style={{ color: "rgba(0,0,0,0.7)", fontSize: "16px" }}>
                                {faq.answer}
                            </p>
                        </div>
                    ))}
                    {/* Note text removed here */}
                </div>
            )}

            {/* Request History Section */}
            <RequestHistory />

            {/* Concierge Modal (Kept) */}
            {showConciergeForm && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "16px",
                            padding: "30px",
                            width: "90%",
                            maxWidth: "500px",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                            position: "relative",
                        }}
                    >
                        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
                            Concierge Service Request
                        </h2>

                        <form onSubmit={handleSubmit}>
                            <input
                                type="text"
                                name="name"
                                placeholder="Full Name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <input
                                type="text"
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <input
                                type="email"
                                name="email"
                                placeholder="Email (optional)"
                                value={formData.email}
                                onChange={handleChange}
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <input
                                type="text"
                                name="serviceType"
                                placeholder="Enter your service type"
                                value={formData.serviceType}
                                onChange={handleChange}
                                required
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <textarea
                                name="description"
                                placeholder="Describe your request"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <input
                                type="date"
                                name="preferredDate"
                                value={formData.preferredDate}
                                onChange={handleChange}
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <textarea
                                name="additionalNotes"
                                placeholder="Additional Notes"
                                value={formData.additionalNotes}
                                onChange={handleChange}
                                style={{ width: "100%", marginBottom: "10px", padding: "10px" }}
                            />

                            <button
                                type="submit"
                                style={{
                                    background: "#007bff",
                                    color: "#fff",
                                    padding: "10px",
                                    border: "none",
                                    width: "100%",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                }}
                            >
                                Submit Request
                            </button>
                        </form>

                        {status && (
                            <p style={{ textAlign: "center", marginTop: "10px" }}>{status}</p>
                        )}

                        <button
                            onClick={() => setShowConciergeForm(false)}
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "15px",
                                background: "transparent",
                                border: "none",
                                fontSize: "20px",
                                cursor: "pointer",
                            }}
                        >
                            ✖
                        </button>
                    </div>
                </div>
            )}

            {/* ✅ Full Page Vendor View - Using Portal (EXACT copy from VendorList) */}
            {selectedVendor && createPortal(
                <div className={vendorStyles.fullPageVendorView}>
                    <div className={vendorStyles.modalContainer}>
                        {/* Vendor Detail Header */}
                        <div className={vendorStyles.vendorDetailHeader}>
                            <h2>{selectedVendor.name}</h2>
                            <button 
                                onClick={() => {
                                    setSelectedVendor(null);
                                    setErrorMessage("");
                                    setSuccessMessages([]);
                                    setServiceMessages({});
                                    setRedeemEmail("");
                                    setRedeemMessage("");
                                }} 
                                className={vendorStyles.vendorDetailCloseBtn}
                            >
                                ×
                            </button>
                        </div>
            
                        <div className={vendorStyles.vendorDetailContent}>
                            {/* Vendor Info Section */}
                            <div className={vendorStyles.vendorInfoSection}>
                                {selectedVendor.logo && (
                                    <div className={vendorStyles.logoContainer}>
                                        <img
                                            src={getValidS3Url(selectedVendor.logo)}
                                            alt={selectedVendor.name}
                                            className={vendorStyles.vendorLogo}
                                        />
                                    </div>
                                )}
                                            
                                <div className={vendorStyles.vendorDetails}>
                                    <p className={vendorStyles.vendorDescription}>
                                        {selectedVendor.description || "No description available"}
                                    </p>
                                    <div className={vendorStyles.categoryInfo}>
                                        <span className={vendorStyles.categoryLabel}>Category:</span>
                                        <span className={vendorStyles.categoryValue}>{selectedVendor.category}</span>
                                    </div>
                                                
                                    {selectedVendor.requirements && (
                                        <div className={vendorStyles.requirementInfo}>
                                            <span className={vendorStyles.requirementLabel}>Requirements:</span>
                                            <span className={vendorStyles.requirementValue}>{selectedVendor.requirements}</span>
                                        </div>
                                    )}
                                                
                                    {selectedVendor.timeline && (
                                        <div className={vendorStyles.timelineInfo}>
                                            <span className={vendorStyles.timelineLabel}>Timeline:</span>
                                            <span className={vendorStyles.timelineValue}>{selectedVendor.timeline}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
            
                            {/* Messages Section */}
                            {(errorMessage || successMessages.length > 0) && (
                                <div className={vendorStyles.messagesSection}>
                                    {errorMessage && (
                                        <div className={vendorStyles.errorMessage}>{errorMessage}</div>
                                    )}
                                    {successMessages.length > 0 && (
                                        <div className={vendorStyles.successMessagesContainer}>
                                            {successMessages.map((msg, index) => (
                                                <div key={`success-${index}`} className={vendorStyles.successMessage}>
                                                    {msg}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
            
                            {/* Contact Form Section */}
                            <div className={vendorStyles.contactFormSection}>
                                <h3 className={vendorStyles.sectionTitle}>Contact Information</h3>
                                <div className={vendorStyles.formFields}>
                                    <div className={vendorStyles.fieldGroup}>
                                        <label htmlFor="redeemEmail" className={vendorStyles.fieldLabel}>Your Email:</label>
                                        <input
                                            id="redeemEmail"
                                            type="email"
                                            value={redeemEmail}
                                            onChange={(e) => setRedeemEmail(e.target.value)}
                                            placeholder="your.email@example.com"
                                            className={vendorStyles.inputField}
                                        />
                                    </div>
                                    <div className={vendorStyles.fieldGroup}>
                                        <label htmlFor="redeemMessage" className={vendorStyles.fieldLabel}>Message (optional):</label>
                                        <textarea
                                            id="redeemMessage"
                                            value={redeemMessage}
                                            onChange={(e) => setRedeemMessage(e.target.value)}
                                            placeholder="Enter your general message here..."
                                            className={vendorStyles.textareaField}
                                        />
                                    </div>
                                </div>
                            </div>
            
                            {/* Services Section */}
                            <div className={vendorStyles.servicesSection}>
                                <h3 className={vendorStyles.sectionTitle}>Services</h3>
                                {selectedVendor.services && selectedVendor.services.length > 0 ? (
                                    <div className={vendorStyles.servicesList}>
                                        {selectedVendor.services.map((service) => (
                                            <div key={service.id} className={vendorStyles.serviceItem} data-service-id={service.id}>
                                                <div className={vendorStyles.serviceInfo}>
                                                    <strong className={vendorStyles.serviceName}>{service.service_name}</strong>
                                                    <p className={vendorStyles.serviceDescription}>{service.short_description}</p>
                                                    <textarea
                                                        placeholder="Add a message for this specific service..."
                                                        value={serviceMessages[service.id] || ""}
                                                        onChange={(e) =>
                                                            setServiceMessages((prev) => ({
                                                                ...prev,
                                                                [service.id]: e.target.value,
                                                            }))
                                                        }
                                                        className={vendorStyles.serviceTextarea}
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleRedeem(service, selectedVendor)}
                                                    className={vendorStyles.redeemBtn}
                                                >
                                                    Send Request
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className={vendorStyles.noServicesText}>No services available.</p>
                                )}
                            </div>
            
                            {/* Deck Download Section */}
                            {selectedVendor.deck_url && (
                                <div className={vendorStyles.deckSection}>
                                    <a
                                        href={getValidDeckUrl(selectedVendor.deck_url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={vendorStyles.downloadBtn}
                                    >
                                        Download Deck
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SuspireSupportNetwork;
